import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Select,
  DatePicker,
  Space,
  Tag,
  Tooltip,
  message,
  Modal,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Switch,
  AutoComplete,
  Input
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BugOutlined,
  WarningOutlined,
  SettingOutlined,
  RocketOutlined,
  HistoryOutlined,
  FileTextOutlined,
  ApiOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../services/logger';
import { safeFormatTimestamp } from '../../utils/timezone';
import './DeviceLogs.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

// Log tipleri - DeviceLogTS model'ine uygun (backend gerçek değerler)
const LOG_TYPE_OPTIONS = [
  { value: 'error', label: 'Hata', color: 'red', icon: <ExclamationCircleOutlined /> },
  { value: 'session', label: 'Seans', color: 'blue', icon: <HistoryOutlined /> },
  { value: 'ota_result', label: 'OTA Sonuç', color: 'purple', icon: <RocketOutlined /> },
  { value: 'system_log', label: 'Sistem', color: 'green', icon: <FileTextOutlined /> },
  { value: 'control_response', label: 'Kontrol Yanıt', color: 'orange', icon: <SettingOutlined /> },
  { value: 'config_response', label: 'Config Yanıt', color: 'cyan', icon: <ApiOutlined /> }
];

// Log seviyeleri - standartlaştırıldı: warn (warning → warn olarak düzeltildi)
const LOG_LEVEL_OPTIONS = [
  { value: 'info', label: 'Info', color: 'processing' },
  { value: 'warn', label: 'Warning', color: 'warning' },
  { value: 'error', label: 'Error', color: 'error' }
];

// Kaynak değerleri - backend gerçek değerler
const SOURCE_OPTIONS = [
  { value: 'mqtt', label: 'MQTT' },
  { value: 'system', label: 'Sistem' }
];

const DeviceLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    device_id: null,
    log_type: null,
    log_level: null,
    source: null,
    start_time: null,
    end_time: null
  });
  const [statistics, setStatistics] = useState({});
  const [devices, setDevices] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10);

  // AutoComplete için state'ler
  const [deviceSearchText, setDeviceSearchText] = useState('');
  const [deviceOptions, setDeviceOptions] = useState([]);

  const { user } = useAuth();

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize
      };
      // Sadece dolu filtreleri ekle
      if (filters.device_id) params.device_id = filters.device_id;
      if (filters.log_type) params.log_type = filters.log_type;
      if (filters.log_level) params.log_level = filters.log_level;
      if (filters.source) params.source = filters.source;
      if (filters.start_time) params.start_time = filters.start_time;
      if (filters.end_time) params.end_time = filters.end_time;

      const response = await axios.get('/api/v1/logs/device', { params });

      if (response.data && response.data.logs) {
        setLogs(response.data.logs);
        setPagination(prev => ({
          ...prev,
          total: response.data.total_count || 0
        }));
      } else {
        setLogs([]);
      }
    } catch (error) {
      logger.error('Device logs fetch error:', error);
      message.error(error.response?.data?.detail || 'Cihaz logları yüklenirken hata oluştu');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.current, filters]);

  const fetchStatistics = useCallback(async () => {
    try {
      const params = {};
      if (filters.device_id) params.device_id = filters.device_id;
      if (filters.start_time) params.start_time = filters.start_time;
      if (filters.end_time) params.end_time = filters.end_time;
      const response = await axios.get('/api/v1/logs/device/stats', { params });
      if (response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      logger.error('Statistics fetch error:', error);
    }
  }, [filters.device_id, filters.start_time, filters.end_time]);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/devices');
      if (response.data && Array.isArray(response.data)) {
        setDevices(response.data);
      }
    } catch (error) {
      logger.error('Devices fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStatistics();
    fetchDevices();
  }, [fetchLogs, fetchStatistics, fetchDevices]);

  // Cihaz arama (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (deviceSearchText && deviceSearchText.trim().length >= 1) {
        searchDevices(deviceSearchText);
      } else {
        setDeviceOptions([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [deviceSearchText]);

  const searchDevices = async (searchText) => {
    try {
      const response = await axios.get('/api/v1/devices', {
        params: { search: searchText, limit: 20 }
      });
      if (response.data && Array.isArray(response.data)) {
        setDeviceOptions(response.data.map(device => ({
          value: device.id.toString(),
          label: `${device.name} (${device.mac_address})`
        })));
      }
    } catch (error) {
      logger.error('Device search error:', error);
    }
  };

  // Auto-refresh
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        fetchLogs();
        fetchStatistics();
      }, refreshInterval * 1000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [autoRefresh, refreshInterval, fetchLogs, fetchStatistics]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      start_time: dates && dates[0] ? dates[0].toISOString() : null,
      end_time: dates && dates[1] ? dates[1].toISOString() : null
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      device_id: null,
      log_type: null,
      log_level: null,
      source: null,
      start_time: null,
      end_time: null
    });
    setDeviceSearchText('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleExport = async () => {
    try {
      const params = { format: 'json' };
      if (filters.device_id) params.device_id = filters.device_id;
      if (filters.log_type) params.log_type = filters.log_type;
      if (filters.log_level) params.log_level = filters.log_level;
      if (filters.start_time) params.start_time = filters.start_time;
      if (filters.end_time) params.end_time = filters.end_time;

      const response = await axios.get('/api/v1/logs/device/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `device_logs_${dayjs().format('YYYY-MM-DD')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Loglar dışa aktarıldı');
    } catch (error) {
      logger.error('Export error:', error);
      message.error('Dışa aktarma hatası');
    }
  };

  const getLogTypeConfig = (type) => {
    return LOG_TYPE_OPTIONS.find(o => o.value === type) || { label: type, color: 'default', icon: <InfoCircleOutlined /> };
  };

  const getLogLevelConfig = (level) => {
    return LOG_LEVEL_OPTIONS.find(o => o.value === level) || { label: level, color: 'default' };
  };

  const columns = [
    {
      title: 'Zaman',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      fixed: 'left',
      render: (ts) => safeFormatTimestamp(ts),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    {
      title: 'Cihaz',
      dataIndex: 'device_id',
      key: 'device_id',
      width: 180,
      render: (deviceId) => {
        const device = devices.find(d => d.id === deviceId);
        return device ? (
          <div>
            <div style={{ fontWeight: 500 }}>{device.name}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>{device.mac_address}</Text>
          </div>
        ) : deviceId;
      },
    },
    {
      title: 'Tip',
      dataIndex: 'log_type',
      key: 'log_type',
      width: 150,
      render: (type) => {
        const config = getLogTypeConfig(type);
        return <Tag color={config.color} icon={config.icon}>{config.label}</Tag>;
      },
    },
    {
      title: 'Seviye',
      dataIndex: 'log_level',
      key: 'log_level',
      width: 90,
      render: (level) => { if (!level) return '-'; const c = getLogLevelConfig(level); return <Tag color={c.color}>{c.label}</Tag>; },
    },
    {
      title: 'Kaynak',
      dataIndex: 'source',
      key: 'source',
      width: 90,
      render: (source) => {
        if (!source) return '-';
        const colorMap = { mqtt: 'geekblue', system: 'volcano' };
        return <Tag color={colorMap[source] || 'default'}>{source}</Tag>;
      },
    },
    {
      title: 'Mesaj',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (msg) => (
        <Tooltip title={msg} placement="topLeft">
          <Text ellipsis>{msg}</Text>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => { setSelectedLog(record); setDetailModalVisible(true); }}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={2}>
          <FileTextOutlined /> Cihaz Logları
        </Typography.Title>
      </div>

      {/* Stats Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <Space size="large" wrap>
              <span>
                <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />
                <Text style={{ marginRight: 4 }}>Toplam</Text>
                <Text strong style={{ fontSize: 18 }}>{statistics.total_logs || 0}</Text>
              </span>
              <span>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                <Text style={{ marginRight: 4 }}>Bugün</Text>
                <Text strong style={{ color: '#52c41a', fontSize: 18 }}>{statistics.today_logs || 0}</Text>
              </span>
              {(statistics.error_logs || 0) > 0 && (
                <span>
                  <Badge status="error" />
                  <Text style={{ marginRight: 4 }}>Hata</Text>
                  <Text strong style={{ color: '#f5222d', fontSize: 18 }}>{statistics.error_logs}</Text>
                </span>
              )}
              <span>
                <Badge status="processing" />
                <Text style={{ marginRight: 4 }}>Aktif Cihaz</Text>
                <Text strong style={{ fontSize: 18 }}>{statistics.active_devices || 0}</Text>
              </span>
            </Space>
            {statistics.by_type && (
              <Space size="small" wrap>
                {Object.entries(statistics.by_type).map(([type, count]) => {
                  const config = getLogTypeConfig(type);
                  return (
                    <Tag key={type} color={config.color} style={{ margin: 0 }}>
                      {config.label}: {count}
                    </Tag>
                  );
                })}
              </Space>
            )}
          </div>
        </Card>

      {/* Filters and Table */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <AutoComplete
            placeholder="Cihaz ara..."
            style={{ width: 200 }}
            options={deviceOptions}
            onSearch={setDeviceSearchText}
            onSelect={(value) => handleFilterChange('device_id', parseInt(value))}
            onChange={(value) => { if (!value) { handleFilterChange('device_id', null); setDeviceSearchText(''); } }}
            allowClear
            filterOption={false}
            value={deviceSearchText}
          />
          <Select
            placeholder="Log Tipi"
            style={{ width: 150 }}
            value={filters.log_type}
            onChange={(value) => handleFilterChange('log_type', value)}
            allowClear
          >
            {LOG_TYPE_OPTIONS.map(o => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Select>
          <Select
            placeholder="Seviye"
            style={{ width: 110 }}
            value={filters.log_level}
            onChange={(value) => handleFilterChange('log_level', value)}
            allowClear
          >
            {LOG_LEVEL_OPTIONS.map(o => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Select>
          <Select
            placeholder="Kaynak"
            style={{ width: 110 }}
            value={filters.source}
            onChange={(value) => handleFilterChange('source', value)}
            allowClear
          >
            {SOURCE_OPTIONS.map(o => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Select>
          <RangePicker
            placeholder={['Başlangıç', 'Bitiş']}
            style={{ width: 280 }}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            size="middle"
          />
        </div>

        {/* Action Buttons */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Space wrap>
            <Button
              icon={<FilterOutlined />}
              onClick={handleClearFilters}
              size="small"
            >
              Temizle
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { fetchLogs(); fetchStatistics(); }}
              loading={loading}
              size="small"
            >
              Yenile
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              size="small"
            >
              Dışa Aktar
            </Button>
          </Space>
          <Space>
            <Switch
              checked={autoRefresh}
              onChange={setAutoRefresh}
              checkedChildren="Auto"
              unCheckedChildren="Auto"
              size="small"
            />
            {autoRefresh && (
              <>
                <Select
                  value={refreshInterval}
                  onChange={setRefreshInterval}
                  style={{ width: 80 }}
                  size="small"
                >
                  <Option value={5}>5 sn</Option>
                  <Option value={10}>10 sn</Option>
                  <Option value={30}>30 sn</Option>
                  <Option value={60}>1 dk</Option>
                </Select>
                <Tag color="green" icon={<ReloadOutlined spin />}>Otomatik</Tag>
              </>
            )}
          </Space>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={logs}
          rowKey={(record) => `${record.device_id}-${record.timestamp}`}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} kayıt`,
          }}
          onChange={(newPagination) => setPagination(newPagination)}
          scroll={{ x: 1100 }}
          size="small"
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Log Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedLog && (
          <div>
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Text type="secondary">Zaman</Text><br />
                <Text>{safeFormatTimestamp(selectedLog.timestamp)}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Cihaz</Text><br />
                <Text>{devices.find(d => d.id === selectedLog.device_id)?.name || selectedLog.device_id}</Text>
              </Col>
              <Col span={8}>
                <Text type="secondary">Tip</Text><br />
                {(() => { const c = getLogTypeConfig(selectedLog.log_type); return <Tag color={c.color} icon={c.icon}>{c.label}</Tag>; })()}
              </Col>
              <Col span={8}>
                <Text type="secondary">Seviye</Text><br />
                {(() => { const c = getLogLevelConfig(selectedLog.log_level); return <Tag color={c.color}>{c.label}</Tag>; })()}
              </Col>
              <Col span={8}>
                <Text type="secondary">Kaynak</Text><br />
                <Tag>{selectedLog.source}</Tag>
              </Col>
            </Row>
            <Divider />
            <Text type="secondary">Mesaj</Text><br />
            <Text>{selectedLog.message}</Text>

            {/* Error details */}
            {selectedLog.log_type === 'error' && (
              <>
                <Divider />
                <Row gutter={[16, 8]}>
                  {selectedLog.error_code && <Col span={8}><Text type="secondary">Hata Kodu</Text><br /><Tag color="red">{selectedLog.error_code}</Tag></Col>}
                  {selectedLog.error_severity && <Col span={8}><Text type="secondary">Seviye</Text><br /><Tag color="orange">{selectedLog.error_severity}</Tag></Col>}
                  {selectedLog.error_message && <Col span={24}><Text type="secondary">Hata Mesajı</Text><br /><Text>{selectedLog.error_message}</Text></Col>}
                </Row>
              </>
            )}

            {/* Session details */}
            {selectedLog.log_type === 'session' && (
              <>
                <Divider />
                <Row gutter={[16, 8]}>
                  {selectedLog.session_shots != null && <Col span={6}><Text type="secondary">Atış</Text><br /><Text strong>{selectedLog.session_shots}</Text></Col>}
                  {selectedLog.session_duration != null && <Col span={6}><Text type="secondary">Süre</Text><br /><Text strong>{Math.floor(selectedLog.session_duration / 60)}dk {selectedLog.session_duration % 60}sn</Text></Col>}
                  {selectedLog.session_joule != null && <Col span={6}><Text type="secondary">Joule</Text><br /><Text strong>{selectedLog.session_joule} J</Text></Col>}
                  {selectedLog.session_hz != null && <Col span={6}><Text type="secondary">Frekans</Text><br /><Text strong>{selectedLog.session_hz} Hz</Text></Col>}
                </Row>
              </>
            )}

            {/* OTA details */}
            {selectedLog.log_type === 'ota_result' && (
              <>
                <Divider />
                <Row gutter={[16, 8]}>
                  {selectedLog.ota_firmware_version && <Col span={8}><Text type="secondary">Firmware</Text><br /><Tag color="blue">{selectedLog.ota_firmware_version}</Tag></Col>}
                  {selectedLog.ota_status && <Col span={8}><Text type="secondary">Durum</Text><br /><Tag color={selectedLog.ota_success ? 'green' : 'red'}>{selectedLog.ota_status}</Tag></Col>}
                  {selectedLog.ota_error_message && <Col span={24}><Text type="secondary">Hata</Text><br /><Text type="danger">{selectedLog.ota_error_message}</Text></Col>}
                </Row>
              </>
            )}

            {/* Control response details */}
            {selectedLog.log_type === 'control_response' && selectedLog.control_response_data && (
              <>
                <Divider />
                <Text type="secondary">Kontrol Yanıtı</Text><br />
                <TextArea value={JSON.stringify(selectedLog.control_response_data, null, 2)} readOnly rows={4} />
              </>
            )}

            {/* Config response details */}
            {selectedLog.log_type === 'config_response' && selectedLog.config_response_data && (
              <>
                <Divider />
                <Text type="secondary">Config Yanıtı</Text><br />
                <TextArea value={JSON.stringify(selectedLog.config_response_data, null, 2)} readOnly rows={4} />
              </>
            )}

            {/* System log details */}
            {selectedLog.log_type === 'system_log' && selectedLog.system_log_message && (
              <>
                <Divider />
                <Text type="secondary">Sistem Mesajı</Text><br />
                <Text code>{selectedLog.system_log_message}</Text>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DeviceLogs;
